import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import EmailIcon from '@mui/icons-material/Email';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import StorageIcon from '@mui/icons-material/Storage';
import { Link } from 'react-router-dom';
import Button from '@mui/material/Button';

export default function WhatCanWeDetect() {
    return (
        <Box
            width="100%"
            sx={{
                background: '#242331',
                display: 'grid',
                placeItems: 'center',
                py: 10,
            }}
        >
            <Grid container maxWidth="lg" spacing={2} justifyContent="center" sx={{ px: 2 }}>
                <Grid item size={{ xs: 12 }} textAlign="center" mb={2}>
                    <Typography
                        variant="overline"
                        color="prevailRed.main"
                        fontWeight="bold"
                        sx={{ letterSpacing: 3, fontSize: '0.9em' }}
                    >
                        CAPABILITIES
                    </Typography>
                    <Typography variant="h3" color="grey.100" fontWeight="bold" mt={1}>
                        What PREVAIL Detects
                    </Typography>
                    <Typography variant="h6" color="grey.500" mt={2} sx={{ maxWidth: '650px', mx: 'auto' }}>
                        PREVAIL detects active attacks against your data and applications —
                        catching threats before they can cause lasting damage.
                    </Typography>
                </Grid>

                {[
                    {
                        icon: <EmailIcon sx={{ fontSize: 44 }} />,
                        title: 'Account Takeover',
                        description:
                            'The most common attack vector against Microsoft 365 tenants begins with a phishing email. Attackers steal credentials and access user accounts — then move quietly through email, files, and contacts. PREVAIL uses machine learning to model each user and tenant, detecting unauthorized access the moment it happens.',
                        examples: ['Phishing & spear-phishing', 'MFA bypass & exhaustion', 'Transparent proxy attacks', 'Credential stuffing'],
                    },
                    {
                        icon: <ManageAccountsIcon sx={{ fontSize: 44 }} />,
                        title: 'Insider Threats',
                        description:
                            'Most data breaches involve a current or former employee, vendor, or third party misusing their access. These insiders exploit their position to steal proprietary data, destroy records, or commit fraud. PREVAIL detects anomalous behavior based on individual baselines — catching threats before they can exploit their access.',
                        examples: ['Data theft before resignation', 'Privilege misuse', 'Accidental data exposure', 'Supply chain compromise'],
                    },
                    {
                        icon: <StorageIcon sx={{ fontSize: 44 }} />,
                        title: 'Data Exfiltration',
                        description:
                            'Once inside an account, attackers move quickly to extract sensitive data. Mass file downloads, SharePoint link abuse, and external sharing of confidential files are all attack patterns PREVAIL monitors. Behavioral baselines mean unusual volume or access patterns are flagged immediately — not weeks later.',
                        examples: ['Mass SharePoint downloads', 'Unauthorized external sharing', 'Unusual file access patterns', 'Ransomware precursor activity'],
                    },
                ].map(({ icon, title, description, examples }) => (
                    <Grid item size={{ xs: 12, md: 4 }} key={title} sx={{ display: 'flex' }}>
                        <Box
                            sx={{
                                background: '#1a1929',
                                border: '1px solid rgba(217,0,0,0.3)',
                                borderRadius: 2,
                                p: 4,
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Typography color="prevailRed.main" mb={2}>{icon}</Typography>
                            <Typography variant="h5" color="grey.100" fontWeight="bold" mb={2}>
                                {title}
                            </Typography>
                            <Typography variant="body2" color="grey.400" lineHeight={1.8} mb={3}>
                                {description}
                            </Typography>
                            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 2, mt: 'auto' }} />
                            {examples.map((ex) => (
                                <Box key={ex} display="flex" alignItems="center" gap={1} mb={0.75}>
                                    <Box
                                        sx={{
                                            width: 6,
                                            height: 6,
                                            borderRadius: '50%',
                                            background: '#d90000',
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Typography variant="caption" color="grey.500">{ex}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Grid>
                ))}

                <Grid item xs={12} textAlign="center" mt={4}>
                    <Link to="/products/ms365">
                        <Button
                            variant="outlined"
                            size="large"
                            sx={{ pl: 5, pr: 5, py: 1.5, borderColor: 'rgba(217,0,0,0.5)', color: 'grey.300' }}
                        >
                            <Typography sx={{ fontSize: '1em' }}>
                                See Full Microsoft 365 Protection Details
                            </Typography>
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </Box>
    );
}
